/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';

import {
    GenericValue, HisDynamicFieldType, HisBaseGlobalsService,
    DynamicComponent, DynamicContent, GUIComponent, GUIComponentField, DynamicObjects, HisDynamicFieldConfig
} from 'hisng';

import { Declaration } from 'app/fs/res/common/model/request-base/request-base';
import { HISDDKEY_DEFAULT } from 'app/fs/res/common/model/constants';
import { ResTabMenuItem } from 'app/fs/res/common/model/core/tab-menu-item';
import { DataDictionaryUtils } from 'app/fs/res/common/utils/data-dictionary-utils';

@Injectable()
export class DynamicContentUtils {

    constructor(
        private hisBaseGlobalsService: HisBaseGlobalsService,
        private ddUtils: DataDictionaryUtils ) {
    }

    /**
     * get the information about visibility via hisBaseGlobalsService of cached data dictionary entries
     *
     * @param hisKeyIds array containing all relevant hisKeyIds, from which the ddKeys for the tabView component are built
     */
    public isTabVisible(hisKeyIds: number[] | string[]): boolean {
        // if at least on hisDdKey for declaration is visible...
        for (let hisKeyId of hisKeyIds) {
            if ( this.ddUtils.isVisible( HISDDKEY_DEFAULT + hisKeyId )) {
                return true;
            }
        }
        return false;
    }

    /* filter tabs on data-dictionary configuration for hidden fields */
    public filteredTabsOnHiddenFields( guiComponentChildren: GUIComponent[] ): GUIComponent[] {
        return guiComponentChildren.filter( ( tabChild: GUIComponent ) =>
            tabChild.children.find(
                ( fieldsetChild: GUIComponent ) =>
                    fieldsetChild.fields.find(
                        ( fieldChild: GUIComponentField ) => this.ddUtils.isVisible( fieldChild.ddKey )
                    )
            )
        );
    }

    public setupDynamicTabsHeader( dynamicComponent: DynamicComponent, tabMenuItems: ResTabMenuItem[], withDynamicClassIdentifiers?: boolean, dynamicApprovalResultIdentifiers?: string [] ) {
        if ( dynamicComponent ) {
            dynamicComponent.guiComponent.children.sort( ( c1, c2 ) => c2.sortorder - c1.sortorder );
            dynamicComponent.guiComponent.children.sort( ( c1, c2 ) => c1.position - c2.position ).forEach( ( tab: GUIComponent ) => {
                if ( tab.children.find( c => c.visibleAttributes === true ) ) {
                    if ( tabMenuItems.map( h => h.i18nKey ).indexOf( tab.name ) <= -1 ) {
                        let tabPosition = ( tab.position === null || tab.position > tabMenuItems.length ) ? tabMenuItems.length : tab.position;
                        if ( withDynamicClassIdentifiers ) {
                            let dynamicClassnameIdentifiers: string[] = this.getDynamicClassIdentifiers( tab.children, dynamicApprovalResultIdentifiers );
                            // console.log( '#DynamicContentUtils.setupDynamicTabsHeader() - dynamicClassnameIdentifiers: ', dynamicClassnameIdentifiers );
                            if ( dynamicClassnameIdentifiers.length !== 0 ) { 
                                tabMenuItems.splice( tabPosition, 0, {
                                    i18nKey: tab.name, isDynamic: true,
                                    dynamicClassnameIdentifiers:  dynamicClassnameIdentifiers } );
                            }
                        } else {
                            tabMenuItems.splice( tabPosition, 0, { i18nKey: tab.name, isDynamic: true, isHidden: !this.atLeatOneTabChildFieldNotHidden( tab.children, dynamicComponent.conditions.contexts ) } );
                        }
                    } else {
                        if ( withDynamicClassIdentifiers ) {
                            tabMenuItems.find( t => t.i18nKey === tab.name ).dynamicClassnameIdentifiers = [ ...this.getDynamicClassIdentifiers( tab.children, dynamicApprovalResultIdentifiers ) ];
                        }
                    }
                } else {
                    let tabMenuEntry = tabMenuItems.find( t => t.i18nKey === tab.name );
                    let tabIndex = tabMenuItems.map( h => h.i18nKey ).indexOf( tab.name );
                    let isDdHidden = this.hisBaseGlobalsService.getDd( tab.name );
                    if( tabMenuEntry?.isDynamic && tabIndex > -1
                        && ( isDdHidden?.hidden || ( isDdHidden === undefined || isDdHidden === null ) ) ) {
                        // console.log('#DynamicContentUtils.setupDynamicTabsHeader() - Remove tab: ', tab.name);
                        tabMenuItems.splice( tabIndex, 1 );
                    }
                }
            } );
        }
    }

    private getDynamicClassIdentifiers( tabChildren: GUIComponent[], dynamicApprovalResultIdentifiers: string[] ): string[] {
        return tabChildren.map( ( fieldsetChild: GUIComponent ) => {
            if ( dynamicApprovalResultIdentifiers.indexOf( fieldsetChild.name ) > -1 ) {
                return fieldsetChild.name;
            } else {
                return null;
            }
        } ).filter( ( identifier: string ) => identifier !== null );
    }

    /* function to check visbility of fields and fieldsets from DD-Keys */
    public atLeatOneTabChildFieldNotHidden( fieldsetChildren: GUIComponent[], contexts: string[] ): boolean {
        let atLeastOnNotHidden = false;
        fieldsetChildren.forEach( ( c: GUIComponent ) => {
            c.fields.forEach( ( f: GUIComponent ) => {
                if ( this.ddUtils.isVisible( f.ddKey, contexts ) ) {
                    atLeastOnNotHidden = true;
                }
            } );
        } ) ;
        return atLeastOnNotHidden;
    }

    public checkForYesNoWithTextArea( guiComponent: GUIComponent, dynamicObjects: DynamicObjects[] ): boolean {
        if ( guiComponent.fields && guiComponent.fields.length === 2 ) {
            let yesNoSelectField: GUIComponentField = guiComponent.fields.find(
                field => field.controlType === HisDynamicFieldType.YesNoSelect );
            let textField: GUIComponentField = guiComponent.fields.find(
                field => field.controlType === HisDynamicFieldType.Text );
            if ( yesNoSelectField && textField ) {
                // check if attributeName for YesNoSelectField and TextField contain number
                let numberRegex = RegExp( /\d+/g );
                if ( yesNoSelectField.attributeName.match( numberRegex ) && textField.attributeName.match( numberRegex ) ) {
                    // check that DD configuration for both is not hidden
                    if ( this.ddUtils.isVisible( yesNoSelectField.ddKey ) && this.ddUtils.isVisible( textField.ddKey ) ) {
                        if ( this.getDynamicObjectAttribute(
                            dynamicObjects, yesNoSelectField.objectUID, yesNoSelectField.attributeName, 'value' ) === '0' ) {
                            this.hisBaseGlobalsService.setRequiredToDdEntry( textField.ddKey, true );
                        }
                        return true;
                    }
                }
            }
        }
        return false;
    }

    public isDynamicContentVisible( tabKey: string, dynamicComponent: DynamicComponent, contexts?: string[] ): boolean {
        let personTabChildren: GUIComponent = dynamicComponent.guiComponent.children.find(
            ( tabChild: GUIComponent ) => tabChild.name === tabKey );
        return this.atLeatOneTabChildFieldNotHidden( personTabChildren ? personTabChildren.children : [], ( contexts ? contexts : dynamicComponent.conditions.contexts ) );
    }

    public isStaticContentVisible( dDKeys: string[], dynamicComponent: DynamicComponent, contexts?: string[] ): boolean {
        let isStaticContentVisible: boolean = false;
        dDKeys.forEach( ( key: string ) => {
            let dDEntry = this.hisBaseGlobalsService.getDd( key, ( contexts ? contexts : dynamicComponent?.conditions.contexts ) );
            if ( !dDEntry || dDEntry && !dDEntry.hidden ) { 
                isStaticContentVisible = true;
            }
        } );
        return isStaticContentVisible;
    }
    /**
     * get referenced dynamic object.
     *
     * @param dynamicObjects array of referenced dynamic objects
     * @param objectUID object guid of referenced object/dynamic_class
     * @param attributeName attribute name of referenced object/dynamic_attribute
     * @param attribute value of dynamic object to return
     */
    public getDynamicObjectAttribute( dynamicObjects: DynamicObjects[], objectUID: string,
        attributeName: string, attribute: string ): string {
        if ( dynamicObjects ) {
            let dynamicObject = dynamicObjects.find( dO => dO.objGuid === objectUID );
            if ( dynamicObject ) {
                let dynamicObjectValue: Record<string, any> = dynamicObject.values.find( dV => dV.attributeName === attributeName );
                return dynamicObjectValue[attribute];
            }
        }
        return null;
    }

    /* get Declaration object from DynamicComponent */
    public convertDynamicComponentToDeclaration( guiComponent: GUIComponent,
        dynamicObjects: DynamicObjects[] ): Declaration[] {
        let dynamicDeclarations: Declaration[] = [];
        let yesNoSelect = guiComponent.fields.find( field => field.controlType === 'YesNoSelect' );
        if ( yesNoSelect ) {
            let declaration = new Declaration();
            declaration.declarationDefiniton = new GenericValue();
            declaration.declarationDefiniton.shorttext = guiComponent.name;
            declaration.declarationDefiniton.hiskeyId = Number.parseInt(
                guiComponent.name.split( '_' ).pop(), 10 );
            declaration.declarationDefiniton.defaulttext = yesNoSelect.label;
            let yesNoSelectFieldValue = this.getDynamicObjectAttribute(
                dynamicObjects, yesNoSelect.objectUID, yesNoSelect.attributeName, 'value' );
            if ( yesNoSelectFieldValue ) {
                declaration.checkboxValue = new GenericValue();
                declaration.checkboxValue.id = yesNoSelectFieldValue === '1' ? 1 : 0;
            } else {
                declaration.checkboxValue = null; // default null, declaration has to own this property
            }
            dynamicDeclarations.push( declaration );
        }

        let textField = guiComponent.fields.find( field => field.controlType === 'Text' );
        if ( textField ) {
            let declaration = new Declaration();
            declaration.declarationDefiniton = new GenericValue();
            declaration.declarationDefiniton.shorttext = guiComponent.label;
            declaration.declarationDefiniton.hiskeyId = Number.parseInt( textField.name.split( '_' ).pop(), 10 );
            let textFieldValue = this.getDynamicObjectAttribute(
                dynamicObjects, textField.objectUID, textField.attributeName, 'value' );
            declaration.textfieldValue = textFieldValue;
            dynamicDeclarations.push( declaration );
        }

        return dynamicDeclarations;
    }

    createDynamicFormFieldConfig(dynamicComponent: DynamicComponent, attributeName: string): HisDynamicFieldConfig {
        if(dynamicComponent.guiComponent.children.length &&
            dynamicComponent.guiComponent.children[0].children.length &&
            dynamicComponent.guiComponent.children[0].children[0].fields.length) {
            const field = dynamicComponent.guiComponent.children[0].children[0].fields.find(f => f.attributeName === attributeName);
            if (field) {
                const dynObj = dynamicComponent.dynamicObjects.find(obj => obj.objGuid === field.objectUID);
                if (dynObj) {
                    const value = dynObj.values.find(v => v.attributeName === attributeName);
                    if (value && field) {
                        return {
                            controlType: ( HisDynamicFieldType as any )[field.controlType],
                            label: field.label,
                            name: field.name,
                            ddKey: field.ddKey,
                            typeAheadUrl: value.source,
                            defaultValue: value.value,
                            oldValue: value.oldValue,
                            useOldValue: value.useOldValue,
                            children: []
                        };
                    }
                }
            }
        }
        return null;
    }

    /* merge dynamicContent of two objects */
    public getCombinedDynamicContent( _dynamicComponentObjectOne: DynamicContent, _dynamicComponentObjectTwo: DynamicContent ): DynamicContent { 
        if ( _dynamicComponentObjectOne._dynamicComponent.guiComponent.visibleAttributes ) { 
            let combinedDynamicContent = new DynamicContent();
            combinedDynamicContent._dynamicComponent = JSON.parse( JSON.stringify( _dynamicComponentObjectOne._dynamicComponent ) );
            _dynamicComponentObjectTwo._dynamicComponent.guiComponent.children.forEach(
                ( tabChildTwo: GUIComponent ) => {
                    let sameTabDefinition = combinedDynamicContent._dynamicComponent.guiComponent.children.find( ( tabChildOne: GUIComponent ) => tabChildOne.name === tabChildTwo.name );
                    if ( sameTabDefinition ) {
                        sameTabDefinition.children.push( ...tabChildTwo.children );
                    } else {
                        combinedDynamicContent._dynamicComponent.guiComponent.children.push( tabChildTwo );
                    }
                }
            );
            _dynamicComponentObjectTwo._dynamicComponent.dynamicObjects.forEach(
                ( dynamicObject: DynamicObjects ) => {
                    combinedDynamicContent._dynamicComponent.dynamicObjects.push( dynamicObject );
                }
            );
            return combinedDynamicContent;
        }
        return _dynamicComponentObjectTwo; 
    }

}
